/**
 * Copyright SimVentions, Inc. Usage, distribution, transferal, and licensing
 * of this source code is protected under SBIR law as described in DFARS 252.227-7018.
 *
 * SBIR data rights fully described in the README.md file in the top level directory of this project.
 */
import * as React from "react";
import { useEffect, useRef, useState } from "react";
import { useStyletron } from "baseui";
import { Button, SIZE } from "baseui/button";
import { Overflow } from "baseui/icon";
import { StatefulMenu } from "baseui/menu";
import { PLACEMENT, StatefulPopover } from "baseui/popover";
import { useDexterTheme } from "../DesignSystem/Theme/ThemeContext";
import { TOP_ITEM_ZINDEX } from "../Utils/SiteProps";

export interface OverflowActions {
  label: string;
  onClick?: () => void;
  disabled?: boolean;
}

export const OverflowMenu = ({
  overflowActions,
  disabled,
  float,
}: {
  overflowActions: OverflowActions[];
  disabled?: boolean;
  float?: "left" | "right" | "none" | "inline-start" | "inline-end";
}): JSX.Element => {
  const [css] = useStyletron();
  const { currentTheme } = useDexterTheme();

  // Track open/closed only so we know when to attach/detach our listener.
  // StatefulPopover still owns the real open state and all its usual behavior.
  const [isOpen, setIsOpen] = useState(false);

  const closeRef = useRef<(() => void) | null>(null);
  const triggerRef = useRef<HTMLDivElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!isOpen) return;

    // Fixes react flow canvas taking over mouse events before closing menu
    const handlePointerDown = (event: MouseEvent): void => {
      const target = event.target as Node;
      const clickedInsideMenu = menuRef.current?.contains(target) ?? false;
      const clickedTrigger = triggerRef.current?.contains(target) ?? false;

      if (!clickedInsideMenu && !clickedTrigger) {
        closeRef.current?.();
      }
    };

    document.addEventListener("mousedown", handlePointerDown, true);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown, true);
    };
  }, [isOpen]);

  return (
    <StatefulPopover
      focusLock
      placement={PLACEMENT.auto}
      onOpen={() => setIsOpen(true)}
      onClose={() => setIsOpen(false)}
      overrides={{
        Body: {
          style: {
            zIndex: TOP_ITEM_ZINDEX,
            backgroundColor: currentTheme.colors.dropdownBackground,
          },
        },
        Arrow: {
          style: {
            fill: currentTheme.colors.dropdownBackground,
          },
        },
      }}
      content={({ close }) => {
        closeRef.current = close;
        return (
          <div ref={menuRef}>
            <StatefulMenu
              items={overflowActions}
              onItemSelect={(opt) => {
                close();
                opt.item.onClick?.();
              }}
              overrides={{
                List: {
                  style: {
                    backgroundColor: currentTheme.colors.dropdownBackground,
                    border: `1px solid ${currentTheme.colors.dropdownBorderColor}`,
                    color: currentTheme.colors.dropdownText,
                  },
                },
                Option: {
                  style: ({ $isHighlighted, $disabled }) => ({
                    backgroundColor: $isHighlighted
                      ? currentTheme.colors.dropdownItemHoverBackground
                      : currentTheme.colors.dropdownBackground,
                    color: $disabled
                      ? currentTheme.colors.helpTextColor
                      : currentTheme.colors.dropdownText,
                    cursor: $disabled ? "not-allowed" : "pointer",
                    boxShadow: $isHighlighted
                      ? "inset 0 0 0 1000px rgba(0, 0, 0, 0.15)"
                      : "none",
                    transition:
                      "box-shadow 0.15s ease, background-color 0.15s ease",
                  }),
                },
              }}
            />
          </div>
        );
      }}
    >
      <div ref={triggerRef} className={css({ float: float ?? "right" })}>
        <Button
          disabled={disabled}
          size={SIZE.compact}
          overrides={{
            BaseButton: {
              style: {
                backgroundColor: "transparent",
                color: currentTheme.colors.text,
                scale: "0.75 1",
                height: "10px",
              },
            },
          }}
        >
          <Overflow
            size={20}
            title=""
            color={currentTheme.colors.text}
            overrides={{
              Svg: {
                style: {
                  transform: "rotate(90deg) scaleY(1.33)",
                },
              },
            }}
          />
        </Button>
      </div>
    </StatefulPopover>
  );
};
